import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Typography, CircularProgress, Alert, Snackbar, Paper,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import apiService from '../services/apiService';

// Keep in sync with the schema maxlength and the server-side truncation in
// backend/src/models/Settings.js.
const MAX_LEN = 4000;

interface OrganizationSettings {
  org_id: string;
  name: string;
  companyContext: string;
}

/**
 * Company Context — admin-editable description of the organization.
 *
 * Reworded around three questions (WHAT you sell, WHO buys it, ROUGHLY WHAT IT
 * COSTS) because the live discovery checklist made the answers load-bearing
 * rather than merely helpful. It is the cached static prefix of every detector
 * bundle, and several checks are undecidable without it: "opened with a pitch"
 * needs to know what the pitch would be, and "gave the strategy away for free"
 * needs to know what the strategy is worth. Price band is what lets the
 * budget-read and pain-with-a-price-tag items tell a serious number from a
 * throwaway one.
 *
 * Also grounds the training simulation and post-call analysis prompts.
 *
 * Rendered as the first tab on the Knowledge Base page.
 */
const CompanyContextCard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [context, setContext] = useState('');
  const [original, setOriginal] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await apiService.get<OrganizationSettings>('/settings/organization');
        if (cancelled) return;
        const ctx = resp?.data?.companyContext ?? '';
        setContext(ctx);
        setOriginal(ctx);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load company context');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dirty = context !== original;
  const remaining = MAX_LEN - context.length;
  const overLimit = remaining < 0;

  const handleSave = async () => {
    if (!dirty || overLimit) return;
    try {
      setSaving(true);
      setError(null);
      const resp = await apiService.put<OrganizationSettings>(
        '/settings/organization',
        { companyContext: context }
      );
      const saved = resp?.data?.companyContext ?? context;
      setOriginal(saved);
      setContext(saved);
      setSavedToast(true);
    } catch (e: any) {
      setError(e?.message || 'Failed to save company context');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Answer three things, in plain language:
        </Typography>
        <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 3, my: 1 }}>
          <li><strong>What you sell</strong> — the products or services, and the problem they remove.</li>
          <li><strong>Who buys it</strong> — the industries, the company size, and the job title you usually sit across from.</li>
          <li><strong>Roughly what it costs</strong> — a typical deal size or price band.</li>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Live call coaching reads this before every call. Without the price
          band it cannot tell a serious budget answer from a throwaway one, and
          without knowing what you sell it cannot tell a pitch from a question.
          Specifics beat marketing language here.
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <TextField
            fullWidth
            multiline
            minRows={8}
            maxRows={18}
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={
              "WHAT WE SELL\n" +
              "Sodium-hydroxide-based industrial cleaning systems for airport " +
              "ground-handling equipment. They displace legacy degreaser " +
              "chemistries that are slower, more hazardous, and harder to " +
              "dispose of. A 3× faster cleaning cycle, fully biodegradable, " +
              "compliant in 40+ jurisdictions.\n\n" +
              "WHO BUYS IT\n" +
              "Operations directors and maintenance leads at airlines and " +
              "ground handlers running 50+ aircraft daily. Usually a director " +
              "or VP of ground operations; procurement and EHS weigh in.\n\n" +
              "ROUGHLY WHAT IT COSTS\n" +
              "$80k–$250k for a first site, plus $30k/yr consumables. Multi-site " +
              "rollouts run $500k+."
            }
            variant="outlined"
            error={overLimit}
            inputProps={{ style: { fontSize: 14 } }}
          />
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mt={2}
          >
            <Typography
              variant="caption"
              color={overLimit ? 'error' : 'text.secondary'}
            >
              {overLimit
                ? `Over by ${-remaining} characters — please trim`
                : `${remaining} characters remaining (max ${MAX_LEN})`}
            </Typography>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={!dirty || overLimit || saving}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Snackbar
        open={savedToast}
        autoHideDuration={3000}
        onClose={() => setSavedToast(false)}
        message="Company context saved"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default CompanyContextCard;
